import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { persistReportScope, readMetricsScope } from '@/application/metrics/metricsScope';
import { useAuth } from '@/presentation/contexts/useAuth';
import { useClinicNames } from '@/presentation/hooks/useClinicNames';
import { useGlobalPeriod } from '@/presentation/hooks/useGlobalPeriod';

export interface ReportScopeFiltersOptions {
  period?: string;
  clinicId?: string;
  /** @deprecated el periodo global ya escribe year/month en la URL. */
  syncYearMonth?: boolean;
  /**
   * Si false, no escribe tenant en la URL de la página actual.
   * El periodo lo persiste `useGlobalPeriod` (chip del shell).
   */
  syncUrl?: boolean;
}

/** Filtros de clínica; el periodo sale de `useGlobalPeriod` (un solo state). */
export function useReportScopeFilters(initial?: ReportScopeFiltersOptions) {
  const { user } = useAuth();
  const clinicNames = useClinicNames();
  const [params, setParams] = useSearchParams();
  const syncUrl = initial?.syncUrl ?? true;
  const global = useGlobalPeriod();

  const clinicOptions = useMemo(
    () =>
      (user?.scope.allowedTenantIds ?? []).map((tenantId) => ({
        value: tenantId,
        label: clinicNames[tenantId] ?? tenantId,
      })),
    [user?.scope.allowedTenantIds, clinicNames]
  );

  const [clinicId, setClinicIdState] = useState(() => {
    return initial?.clinicId || params.get('tenant') || readMetricsScope().clinicId || '';
  });

  const urlClinic = params.get('tenant');
  useEffect(() => {
    if (urlClinic) {
      setClinicIdState((current) => (current === urlClinic ? current : urlClinic));
    }
  }, [urlClinic]);

  useEffect(() => {
    if (!clinicId && clinicOptions[0]?.value) {
      setClinicIdState(clinicOptions[0].value);
    }
  }, [clinicId, clinicOptions]);

  useEffect(() => {
    if (clinicId) {
      persistReportScope(global.period, clinicId);
    }

    if (!syncUrl) {
      return;
    }

    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        let changed = false;
        if (clinicId && next.get('tenant') !== clinicId) {
          next.set('tenant', clinicId);
          changed = true;
        }
        return changed ? next : prev;
      },
      { replace: true }
    );
  }, [clinicId, global.period, syncUrl, setParams]);

  const setClinicId = useCallback((value: string) => {
    setClinicIdState(value);
  }, []);

  const hubHref = useMemo(() => '/', []);

  return {
    period: global.period,
    setPeriod: global.setPeriod,
    clinicId,
    setClinicId,
    periodOptions: global.periodOptions,
    clinicOptions,
    year: global.year,
    month: global.month,
    isMultiClinic: clinicOptions.length > 1,
    hubHref,
  };
}
